import { API_BASE_URL } from '@/config';
import {
  clearRememberedProfiles,
  getRoleDashboardPath,
  profileToRole,
  rememberProfileForRole,
} from '@/lib/authRouting';
import { clearAuthenticatedSession } from '@/lib/authSession';
import { queryClient } from '@/lib/queryClient';
import {
  DEFAULT_FEATURE_PERMISSIONS,
  type FeaturePermissionKey,
  type FeaturePermissions,
  type UserProfile,
  type UserRole,
} from '@ergshoot/shared';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type User = {
  id: string;
  email: string;
  name: string;
  username?: string;
  role: UserRole;
  // Multi-role profile fields
  profiles?: UserProfile[];
  active_profile?: UserProfile | null;
  active_profile_id?: string | null;
  // FEAT-APR08-001: freemium subscription state
  // subscription_status: 'active' | 'trialing' | 'pending' | 'past_due' | 'cancelled' | 'paused' | string
  // subscription_source: 'self' (own plan) | 'gym' (inherited from owner) | 'bypass' (HQ-managed)
  // is_frozen: derived — true when status is in {pending, past_due, cancelled, paused}
  subscription_status?: string;
  subscription_source?: 'self' | 'gym' | 'bypass';
  billing_state?: 'pending_approval' | null;
  is_frozen?: boolean;
};

export type PermissionKey = FeaturePermissionKey;
export type Permissions = FeaturePermissions;

// FEAT-APR08-002 — raw coach permission flags surfaced from /auth/permissions
// Owners get all flags ON automatically; coaches get the per-row JSON merged
// with sensible defaults. Sidebar / pages gate on these directly.
export type CoachPermissionFlag =
  | 'can_create_workouts'
  | 'can_invite'
  | 'can_create_events'
  | 'can_approve_scores'
  | 'can_view_billing'
  | 'can_manage_coaches'
  | 'can_manage_members'
  | 'can_log_scores_for_members'
  | 'can_post_announcements'
  | 'can_offer_discounts';
export type CoachPermissions = Record<CoachPermissionFlag, boolean>;

type AuthState = {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  // Base44-aligned permissions
  permissions: Permissions | null;
  coachPermissions: CoachPermissions | null;
  permissionsLoading: boolean;
  // Multi-role profile fields (derived from user for convenience)
  profiles: UserProfile[];
  activeProfile: UserProfile | null;
  // Session-scoped role filter: null = ALL roles active, array = only those roles visible
  sessionActiveRoles: UserRole[] | null;
  setAuth: (user: User, accessToken: string, refreshToken: string) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  logout: () => void;
  clearAuth: () => void;
  updateToken: (accessToken: string) => void;
  setPermissions: (permissions: Permissions) => void;
  setCoachPermissions: (coachPermissions: CoachPermissions | null) => void;
  setPermissionsLoading: (loading: boolean) => void;
  // Multi-role profile actions
  setProfiles: (profiles: UserProfile[], activeProfile?: UserProfile | null) => void;
  switchProfile: (profileId: string) => Promise<void>;
  setSessionActiveRoles: (roles: UserRole[] | null) => void;
};

export const DEFAULT_ROLE_PERMISSIONS = DEFAULT_FEATURE_PERMISSIONS;

// Rehydrate sessionActiveRoles from sessionStorage (survives page refresh within same tab)
function getInitialSessionActiveRoles(): UserRole[] | null {
  try {
    const stored = sessionStorage.getItem('ergshoot_session_roles');
    if (stored) {
      return JSON.parse(stored) as UserRole[];
    }
  } catch {
    // ignore parse errors
  }
  return null;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      permissions: null,
      coachPermissions: null,
      permissionsLoading: false,
      profiles: [],
      activeProfile: null,
      sessionActiveRoles: getInitialSessionActiveRoles(),
      setAuth: (user, accessToken, refreshToken) => {
        // Seed last-profile memory from the current active profile so multi-gym
        // owners keep their gym after refresh / re-auth.
        rememberProfileForRole(user.active_profile);
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          // Effective permissions are server-authoritative and must be loaded
          // with the authenticated /auth/permissions request.
          permissions: null,
          coachPermissions: null,
          profiles: user.profiles || [],
          activeProfile: user.active_profile || null,
        });
      },
      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),
      logout: () => {
        clearAuthenticatedSession();
        sessionStorage.removeItem('ergshoot_session_roles');
        clearRememberedProfiles();
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          permissions: null,
          profiles: [],
          activeProfile: null,
          sessionActiveRoles: null,
        });
      },
      clearAuth: () => {
        sessionStorage.removeItem('ergshoot_session_roles');
        clearRememberedProfiles();
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          permissions: null,
          profiles: [],
          activeProfile: null,
          sessionActiveRoles: null,
        });
      },
      updateToken: (accessToken) => set({ accessToken }),
      setPermissions: (permissions) => set({ permissions }),
      setCoachPermissions: (coachPermissions) => set({ coachPermissions }),
      setPermissionsLoading: (loading) => set({ permissionsLoading: loading }),

      // Multi-role profile actions
      setProfiles: (profiles, activeProfile) =>
        set({
          profiles,
          activeProfile: activeProfile || profiles.find((p) => p.is_primary) || profiles[0] || null,
        }),

      switchProfile: async (profileId: string) => {
        const state = get();
        const { profiles } = state;

        // Get fresh token from Clerk (stored accessToken is empty with Clerk auth)
        const token = await window.Clerk?.session?.getToken();
        if (!token) {
          throw new Error('Not authenticated');
        }

        // Find the profile in current profiles
        const targetProfile = profiles.find((p) => p.id === profileId);
        if (!targetProfile) {
          throw new Error('Profile not found');
        }

        // Call API to switch profile
        const response = await fetch(`${API_BASE_URL}/profiles/switch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ profile_id: profileId }),
        });

        if (response.status === 401) {
          throw new Error('Your session has expired');
        }

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to switch profile');
        }

        const data = await response.json();
        const nextProfile = data.data.active_profile as UserProfile;

        // Clear role toggle to prevent stale portal state after switching
        sessionStorage.removeItem('ergshoot_session_roles');

        // Remember per-role selection so multi-gym owners return to the same gym
        // after visiting HQ Admin (or another role) and switching back.
        rememberProfileForRole(nextProfile);

        // Update state with new active profile
        set({
          activeProfile: nextProfile,
          sessionActiveRoles: null,
          permissions: null,
          coachPermissions: null,
          user: state.user
            ? {
                ...state.user,
                active_profile: nextProfile,
                active_profile_id: nextProfile.id,
              }
            : null,
        });

        // Gym-specific queries use the active profile implicitly. Reset them
        // only after the new profile is in state so previous-gym data and
        // in-flight requests cannot remain visible under the new context.
        await queryClient.resetQueries();
      },

      setSessionActiveRoles: (roles: UserRole[] | null) => {
        // Persist to sessionStorage so it survives page refreshes but clears on tab close
        if (roles) {
          sessionStorage.setItem('ergshoot_session_roles', JSON.stringify(roles));
        } else {
          sessionStorage.removeItem('ergshoot_session_roles');
        }

        // Role toggles only change portal presentation. Authorization remains
        // the server-computed union of every active role/profile.
        set({ sessionActiveRoles: roles });
      },
    }),
    {
      name: 'ergshoot-auth',
      // sessionActiveRoles is session-scoped (managed via sessionStorage separately)
      // Exclude from localStorage persist to prevent stale toggle state across browser sessions
      partialize: (state) => {
        const { sessionActiveRoles, ...rest } = state;
        return rest;
      },
    }
  )
);

export { getRoleDashboardPath };

// Helper to check if user has a specific permission
export function hasPermission(permissions: Permissions | null, permission: PermissionKey): boolean {
  if (!permissions) return false;
  return permissions[permission] === true;
}

/**
 * Get the effective display role considering the role toggle bar.
 * When sessionActiveRoles has exactly 1 role, that's the "view-as" role.
 * Otherwise, use the active profile role before falling back to legacy user.role.
 */
export function getEffectiveRole(
  userRole: UserRole | undefined,
  activeProfile: UserProfile | null,
  sessionActiveRoles: UserRole[] | null
): UserRole | undefined {
  if (sessionActiveRoles && sessionActiveRoles.length === 1) {
    return sessionActiveRoles[0];
  }

  if (activeProfile) {
    return profileToRole(activeProfile);
  }

  return userRole;
}

export function useEffectiveRole(): UserRole | undefined {
  const userRole = useAuthStore((s) => s.user?.role);
  const activeProfile = useAuthStore((s) => s.activeProfile);
  const sessionActiveRoles = useAuthStore((s) => s.sessionActiveRoles);

  return getEffectiveRole(userRole, activeProfile, sessionActiveRoles);
}
